import {
  useTechnicianPerformance,
  TechScoreboardTotals,
} from '@/hooks/useTechnicianPerformance';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { MetricCard, MetricComparison } from '@/components/reports/MetricCard';
import { calculateChange } from '@/utils/periodComparison';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Briefcase,
  DollarSign,
  Trophy,
  Percent,
  Receipt,
  RotateCcw,
} from 'lucide-react';

interface TechniciansTabProps {
  dateFrom: Date;
  dateTo: Date;
  compareEnabled?: boolean;
}

const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

export const TechniciansTab = ({
  dateFrom,
  dateTo,
  compareEnabled = false,
}: TechniciansTabProps) => {
  const { timezone } = useTimezoneUtils();
  const { formatMoney: formatCurrency } = useCompanyLocale();
  const { data, isLoading } = useTechnicianPerformance(dateFrom, dateTo, timezone, compareEnabled);

  const getComparison = (
    key: keyof TechScoreboardTotals,
    isPositiveGood = true
  ): MetricComparison | undefined => {
    if (!compareEnabled || !data?.previousTotals || !data.comparisonPeriod) return undefined;
    const change = calculateChange(data.totals[key], data.previousTotals[key]);
    return { ...change, label: data.comparisonPeriod.label, isPositiveGood };
  };

  if (isLoading) {
    return <div className="h-64 flex items-center justify-center">Loading Technician Data...</div>;
  }

  const totals = data?.totals;
  const techs = data?.techs || [];
  if (!totals) return null;

  return (
    <div className="space-y-[22px]">
      {/* Technician Scoreboard — company-wide totals across all techs */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        <MetricCard
          title="Total Jobs"
          value={totals.totalJobs}
          icon={Briefcase}
          iconBgColor="bg-primary/10"
          iconColor="text-primary"
          tooltip="Jobs assigned to technicians, by scheduled date"
          comparison={getComparison('totalJobs')}
        />
        <MetricCard
          title="Total Revenue"
          value={formatCurrency(totals.totalRevenue)}
          icon={DollarSign}
          iconBgColor="bg-success/10"
          iconColor="text-success"
          tooltip="Invoice total of completed jobs, by job date"
          comparison={getComparison('totalRevenue')}
        />
        <MetricCard
          title="Total Sales"
          value={totals.totalSales}
          icon={Trophy}
          iconBgColor="bg-warning/10"
          iconColor="text-warning"
          tooltip="Jobs completed (won) by technicians in this period"
          comparison={getComparison('totalSales')}
        />
        <MetricCard
          title="Conversion Rate"
          value={`${totals.conversionRate.toFixed(1)}%`}
          icon={Percent}
          iconBgColor="bg-info/10"
          iconColor="text-info"
          tooltip="Completed jobs ÷ jobs assigned to technicians"
          comparison={getComparison('conversionRate')}
        />
        <MetricCard
          title="Average Ticket"
          value={formatCurrency(totals.avgTicket)}
          icon={Receipt}
          iconBgColor="bg-primary/10"
          iconColor="text-primary"
          tooltip="Revenue per completed job across all technicians"
          comparison={getComparison('avgTicket')}
        />
        <MetricCard
          title="Recalls"
          value={totals.recalls}
          icon={RotateCcw}
          valueColor={totals.recalls > 0 ? 'text-destructive' : undefined}
          iconBgColor={totals.recalls > 0 ? 'bg-destructive/10' : undefined}
          iconColor={totals.recalls > 0 ? 'text-destructive' : undefined}
          tooltip="Jobs manually flagged as recall (warranty / return visit)"
          comparison={getComparison('recalls', false)}
        />
      </div>

      {/* Per-technician breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Technician overview</CardTitle>
          <CardDescription>Individual performance for the selected period</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Technician</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Avg Ticket</TableHead>
                <TableHead className="text-right">Close Rate</TableHead>
                <TableHead className="text-right">Jobs</TableHead>
                <TableHead className="text-right">Sales Won</TableHead>
                <TableHead className="text-right">Recalls</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {techs.map((tech) => (
                <TableRow key={tech.techId}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-md bg-sidebar text-[10px] font-semibold text-sidebar-foreground">
                        {getInitials(tech.techName)}
                      </span>
                      <span className="font-medium">{tech.techName}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {formatCurrency(tech.revenue)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(tech.avgTicket)}
                  </TableCell>
                  <TableCell className="text-right">
                    {tech.totalJobs > 0 ? (
                      <div className="flex items-center justify-end gap-2">
                        <div className="h-1.5 w-14 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{
                              width: `${Math.min(100, Math.max(0, tech.closeRate))}%`,
                            }}
                          />
                        </div>
                        <span className="tabular-nums">{tech.closeRate.toFixed(1)}%</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{tech.totalJobs}</TableCell>
                  <TableCell className="text-right tabular-nums">{tech.completedJobs}</TableCell>
                  <TableCell
                    className={`text-right tabular-nums ${tech.recalls > 0 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}
                  >
                    {tech.recalls}
                  </TableCell>
                </TableRow>
              ))}
              {techs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground h-24">
                    No technician activity in this period
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {techs.length > 0 && (
              <TableFooter className="bg-background">
                <TableRow className="hover:bg-background">
                  <TableCell className="font-semibold">Totals</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {formatCurrency(totals.totalRevenue)}
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {formatCurrency(totals.avgTicket)}
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {totals.totalJobs > 0 ? `${totals.conversionRate.toFixed(1)}%` : '-'}
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {totals.totalJobs}
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {totals.totalSales}
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {totals.recalls}
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
